import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import useOptions from "features/masterData/hooks/useOptions";
import { deleteKeysByList, generateRandomString } from "utils/helper";
import { PAGE_TYPE } from "../schemas/incomeDeductionSchema";
import * as services from "../service/incomeDeductionApi";
import {
  setCurrentIncomeDeduction,
  setIsLoading,
} from "../slices/payrollSlice";
import { message } from "antd";
import { useTranslation } from "react-i18next";

function toInitialValues(initialValues = {}) {
  const {
    taxCalculation = {},
    frequencyOfPayment = {},
    incomeType = {},
    rounding = {},
    ...rest
  } = initialValues;

  const modifiedValues = {
    ...rest,
    taxCalculation: taxCalculation.id,
    rounding: rounding.id,
    incomeType: incomeType.id,
    frequencyOfPayment: frequencyOfPayment.id,
    calculateByActualWorkdays: initialValues.calculateByActualWorkdays === "Y",
    calculateOnce: initialValues.calculateOnce === "Y",
    socialSecurityFund: initialValues.socialSecurityFund === "Y",
    providentFund: initialValues.providentFund === "Y",
    overTime: initialValues.overTime === "Y",
    lateOrAbsentDeduction: initialValues.lateOrAbsentDeduction === "Y",
  };
  console.log("modifiedValues2", modifiedValues);

  return modifiedValues;
}

function toParams(params = {}) {
  let modifiedParams = {
    ...params,
    incomeDeductionId: params.incomeDeducationId,
    incomeDeductinType: params.incomeType,
    calculateOnce: params.calculateOnce ? "Y" : "N",
    calculateByActualWorkdays: params.calculateByActualWorkdays ? "Y" : "N",
    lateOrAbsentDeduction: params.lateOrAbsentDeduction ? "Y" : "N",
    overTime: params.overTime ? "Y" : "N",
    providentFund: params.providentFund ? "Y" : "N",
    socialSecurityFund: params.socialSecurityFund ? "Y" : "N",
    type: PAGE_TYPE.INCOME,
  };
  return deleteKeysByList(modifiedParams, ["offPeriod", "previousPeriod"]);
}

// DBRef objects from the API -> flat ids used by the form
// DBRef objects from the API -> flat ids used by the form
// incomes[].id / deduction.id = _id ของแถวใน income[] / deduction[]
const toLateManagementForm = (lm) => ({
  ...lm,
  deductionTypeId: lm?.deductionType?.id,
  incomeIds: lm?.incomes?.map((o) => o.id) ?? [],
  deductionId: lm?.deduction?.id,
});

// incomeIds / deductionId = _id ของแถวที่ save แล้วใน income[] / deduction[]
const toLateManagementPayload = ({ deductionType, incomes, deduction, ...rest } = {}) =>
  rest.calculateDeductionBy === "Custom"
    ? rest
    : { ...rest, tiers: [], overLimitAction: undefined, overLimitAmount: undefined };
function useIncome() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const isLoading = useSelector((state) => state.payroll.isLoading);
  const currentIncomeDeduction = useSelector((state) => state.payroll.currentIncomeDeduction);
  const {
    incomeDeductionByTypeOptions = [],
    deductionByTypeOptions = [],
    incomeByCompanyOptions = [],
    deductByCompanyOptions = [],
    commonTypeByGroupType = [],
    noAmountOptions = [],
    noWorkHoursOptions = [],
    noWorkDaysOptions = [],
    roundingTypeOptions = [],
    employmentSetupOptions = [],
    frequencyOfPaymentOptions = [],
    taxCalculationOptions = [],
    getIncomeDeductionByTypeOptions,
    getDeductionByTypeOptions,
    getIncomeByCompanyOptions,
    getDeductByCompanyOptions,
    getCommonTypeByGroupType,
    getRoundingTypeOptions,
    getEmploymentAllOptions,
    getFrequencyOfPaymentOptions,
    getTaxCalculationOptions,
  } = useOptions();

  const [employmentDataByKey, setEmploymentDataByKey] = useState({ ...employmentSetupOptions[0] });


  const initialValues = useMemo(() => {
    return toInitialValues(currentIncomeDeduction);
  }, [currentIncomeDeduction]);

  const getIncome = useCallback(
    async (incomeId) => {
      try {
        dispatch(setIsLoading(true));
        if (incomeId) {
          const values = {
            incomeDeductionId: incomeId,
            requestId: generateRandomString(),
            companyId: currentCompany,
            type: PAGE_TYPE.INCOME,
          };
          const response = await services.getIncomeDeduction(values);
          dispatch(setCurrentIncomeDeduction(response.data));
        }
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const onSubmit = useCallback(
    async (values) => {
      try {
        const modifiedValues = {
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: currentEmployee,
          actionId: currentEmployee,
          ...values,
          id: values?.id == null ? null : values?.id,
          employmentTypeSetupId: values?.id == null ? null : values?.id,
          daysForDividingPermit: values?.daysForDividingPermit === true ? "Y" : "N",
          daysForMultiplyingPermit: values?.daysForMultiplyingPermit === true ? "Y" : "N",
          overtime: {
            ...values?.overtime,
            "fixRateOT": values?.overtime?.fixRateOT === true ? "Y" : "N",
            "otAuto": values?.overtime?.otAuto === true ? "Y" : "N",
            "setUpConditionsForTraditionalHolidays": values?.overtime?.setUpConditionsForTraditionalHolidays === true ? "Y" : "N",
          },
          late: {
            ...values?.late,
          },
          lateManagement: toLateManagementPayload(values?.lateManagement),
          absence: {
            ...values?.absence,
          },
          noStamp: {
            ...values?.noStamp,
          },
          early: {
            ...values?.early,
          },
          statusDuringBreaksCalculation: {
            ...values?.statusDuringBreaksCalculation,
          },
          income: values?.incomes?.map(o => {
            let id = o?.id?.startsWith("X") ? null : o?.id;
            return {
              ...o,
              id
            }
          }),
          deduction: values?.deductions?.map(o => {
            let id = o?.id?.startsWith("X") ? null : o?.id;
            return {
              ...o,
              id
            }
          }),
          statusFlag: values?.isActive,
          thisEmploymentIncomeTypeSetting: values?.thisEmploymentIncomeTypeSetting === true || values?.thisEmploymentIncomeTypeSetting === "Y" ? "Y" : "N",
        };
        dispatch(setIsLoading(true));

        if (modifiedValues?.id == null) {
          await services.createEmploymentType(JSON.parse(JSON.stringify(modifiedValues)));
        } else {
          await services.updateEmploymentType(JSON.parse(JSON.stringify(modifiedValues)));
        }

        await getEmploymentAllOptions();
        message.success(t("common.success"))
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, getEmploymentAllOptions]
  );

  const onCancel = useCallback(() => {
    navigate("/configuration");
  }, [navigate]);

  useEffect(() => {
    if (employmentSetupOptions[0]) {
      const deductIdLate = employmentSetupOptions[0].late?.deductionType?.id;
      const deductIdNoStamp = employmentSetupOptions[0].noStamp?.deductionType?.id;
      const deductIdEarly = employmentSetupOptions[0].early?.deductionType?.id;
      const deductIdStatusDuringBreaksCalculation = employmentSetupOptions[0].statusDuringBreaksCalculation?.deductionForEarlyWorkoutOrLateWorkInDuringBreak?.id;
      const deductionForLateWorkInAsAbsentId = employmentSetupOptions[0].absence?.deductionForLateWorkInAsAbsent?.id;
      const deductionForNoTimestampAsAbsentId = employmentSetupOptions[0].absence?.deductionForNoTimestampAsAbsent?.id;
      const incomeType = employmentSetupOptions[0]?.income?.map((value, index) => {
        const id = value?.income?.id
        const type = value?.incomeType?.id
        const tax = value?.deductionWithholdingTax
        let incomeA = {
          id: value?.id,
          incomeId: id,
          incomeTypeId: type,
          deductionWithholdingTax: tax
        }
        return incomeA
      })
      const deductionType = employmentSetupOptions[0]?.deduction?.map((value, index) => {
        const id = value?.deduction?.id
        const type = value?.deductionType?.id
        let deductionA = {
          id: value?.id,
          deductionId: id,
          deductionTypeId: type
        }
        return deductionA
      })
      setEmploymentDataByKey({
        ...employmentSetupOptions[0],
        late: {
          ...employmentSetupOptions[0].late,
          deductionTypeId: deductIdLate,
        },
        lateManagement: toLateManagementForm(employmentSetupOptions[0].lateManagement),
        absence: {
          ...employmentSetupOptions[0].absence,
          deductionForLateWorkInAsAbsentId: deductionForLateWorkInAsAbsentId,
          deductionForNoTimestampAsAbsentId: deductionForNoTimestampAsAbsentId,
        },
        noStamp: {
          ...employmentSetupOptions[0].noStamp,
          deductionTypeId: deductIdNoStamp,
        }, early: {
          ...employmentSetupOptions[0].early,
          deductionTypeId: deductIdEarly,
        }, statusDuringBreaksCalculation: {
          ...employmentSetupOptions[0].statusDuringBreaksCalculation,
          deductionForEarlyWorkoutOrLateWorkInDuringBreakId: deductIdStatusDuringBreaksCalculation,
        },
        income: incomeType,
        deduction: deductionType
      });
    } else {
      setEmploymentDataByKey(employmentSetupOptions[0]);
    }
  }, [employmentSetupOptions]);

  const handleMenuChange = useCallback((values) => {
    const data = employmentSetupOptions.find((s) => s.id === values);
    if (data) {
      const deductIdLate = data.late?.deductionType?.id;
      const deductIdNoStamp = data.noStamp?.deductionType?.id;
      const deductIdEarly = data.early?.deductionType?.id;
      const deductIdStatusDuringBreaksCalculation = data.statusDuringBreaksCalculation?.deductionForEarlyWorkoutOrLateWorkInDuringBreak?.id;
      const deductionForLateWorkInAsAbsentId = data.absence?.deductionForLateWorkInAsAbsent?.id;
      const deductionForNoTimestampAsAbsentId = data.absence?.deductionForNoTimestampAsAbsent?.id;
      const incomeType = data?.income?.map((value, index) => {
        const id = value?.income?.id
        const type = value?.incomeType?.id
        const tax = value?.deductionWithholdingTax
        let incomeA = {
          id: value?.id,
          incomeId: id,
          incomeTypeId: type,
          deductionWithholdingTax: tax
        }
        return incomeA
      })
      const deductionType = data?.deduction?.map((value, index) => {
        const id = value?.deduction?.id
        const type = value?.deductionType?.id
        let deductionA = {
          id: value?.id,
          deductionId: id,
          deductionTypeId: type
        }
        return deductionA
      })
      setEmploymentDataByKey({
        ...data,
        late: {
          ...data?.late,
          deductionTypeId: deductIdLate,
        },
        lateManagement: toLateManagementForm(data?.lateManagement),
        absence: {
          ...data?.absence,
          deductionForLateWorkInAsAbsentId: deductionForLateWorkInAsAbsentId,
          deductionForNoTimestampAsAbsentId: deductionForNoTimestampAsAbsentId,
        },
        noStamp: {
          ...data?.noStamp,
          deductionTypeId: deductIdNoStamp,
        }, early: {
          ...data?.early,
          deductionTypeId: deductIdEarly,
        }, statusDuringBreaksCalculation: {
          ...data?.statusDuringBreaksCalculation,
          deductionForEarlyWorkoutOrLateWorkInDuringBreakId: deductIdStatusDuringBreaksCalculation,
        },
        income: incomeType,
        deduction: deductionType
      });
    } else {
      setEmploymentDataByKey(data);
    }
  }, [employmentSetupOptions]);

  const handleAddMenu = useCallback((values) => {
    setEmploymentDataByKey(null);
  }, []);

  useEffect(() => {
    if (typeof currentCompany !== "object" && params.id) {
      getIncome(params.id);
    }
  }, [currentCompany, params.id, getIncome]);

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getIncomeDeductionByTypeOptions({
        requestId: generateRandomString(),
        companyId: currentCompany,
        type: PAGE_TYPE.INCOME,
      });
    }
  }, [currentCompany, getIncomeDeductionByTypeOptions]);

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getDeductionByTypeOptions({
        requestId: generateRandomString(),
        companyId: currentCompany,
        type: PAGE_TYPE.DEDUCTION,
      });
    }
  }, [currentCompany, getDeductionByTypeOptions]);

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getIncomeByCompanyOptions({
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: currentEmployee,
        actionId: currentEmployee,
      });
    }
  }, [currentCompany, currentEmployee, getIncomeByCompanyOptions]);

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getDeductByCompanyOptions({
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: currentEmployee,
        actionId: currentEmployee,
      });
    }
  }, [currentCompany, currentEmployee, getDeductByCompanyOptions]);
  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getCommonTypeByGroupType({
        requestId: generateRandomString(),
        employeeId: currentEmployee,
        actionId: currentEmployee,
        companyId: currentCompany,
        groupType: "DEDUCTION_TYPE"
      });
    }
  }, [currentCompany, currentEmployee, getCommonTypeByGroupType]);

  useEffect(() => {
    getRoundingTypeOptions();
  }, [getRoundingTypeOptions]);

  useEffect(() => {
    if (currentEmployee && currentCompany) {
      getEmploymentAllOptions();
    }
  }, [getEmploymentAllOptions, currentEmployee, currentCompany]);

  useEffect(() => {
    getFrequencyOfPaymentOptions();
  }, [getFrequencyOfPaymentOptions]);

  useEffect(() => {
    getTaxCalculationOptions();
  }, [getTaxCalculationOptions]);

  return {
    isLoading,
    initialValues,
    roundingTypeOptions,
    employmentSetupOptions,
    employmentDataByKey,
    incomeDeductionByTypeOptions,
    noAmountOptions,
    noWorkHoursOptions,
    noWorkDaysOptions,
    commonTypeByGroupType,
    deductionByTypeOptions,
    incomeByCompanyOptions,
    deductByCompanyOptions,
    frequencyOfPaymentOptions,
    taxCalculationOptions,
    handleAddMenu,
    handleMenuChange,
    onSubmit,
    onCancel,
  };
}

export default useIncome;